import { environment } from './../../environments/environment';
import { JwtUtils } from './utilities/jwtUtils';
import { LoggingService } from './../core/services/logging.service';
import { ToastrService } from 'ngx-toastr';
import { HttpClient } from '@angular/common/http';
import { Roles } from './roles.enum';
import { BroadcastService, MsalService } from '@azure/msal-angular';
import { Injectable } from '@angular/core';
import 'rxjs/add/operator/toPromise';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { User } from 'msal';
import { Applications } from '../core/constants/applications';
import { Router } from '@angular/router';

import { Observable } from 'rxjs/Observable';
import { UserAccount } from './users/user-account';
import { UserService } from './users/user.service';
import { Area } from '../core/constants/area';
import { AuthResponse } from './models/auth-response.model';
import { LocalStorageKeys } from '../core/constants/localStorageKeys.enum';
import { EntraIdLogin } from './models/entra-id-login.model';
import { AuthResponseCode } from './constants/auth-response-code.enum';
import { ConsoleLogger } from '../core/utilities/console-logger';
import { tap } from 'rxjs/operators/tap';
import { CommonService } from '../shared/common/common.service';
import { SignalRService } from '../shared/signalR/signalR.service';
import { EnvironmentSettingsService } from '../shared-services/environment-settings.service';

@Injectable()
export class AuthService {
  private currentUserSubject: BehaviorSubject<UserAccount> = new BehaviorSubject(null);
  private tokenRetrievalInterval = null;
  currentUser$: Observable<UserAccount> = this.currentUserSubject.asObservable();

  private isRefreshingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isRefreshing$: Observable<boolean> = this.isRefreshingSubject.asObservable();

  public get isEmployeeLogin() {
    const val = localStorage.getItem(LocalStorageKeys.IS_EMPLOYEE);
    return !!val && val === 'true';
  }

  public get currentUser(): UserAccount {
    return this.currentUserSubject.getValue();
  }



  public get currentUserId(): number {
    if (!this.currentUser) {
      return null;
    }
    return this.currentUser.id;
  }

  public get currentUserEmail(): string {
    if (!this.currentUser) {
      return null;
    }
    return this.currentUser.email;
  }

  public get currentUserName(): string {
    if (!this.currentUser) {
      return null;
    }
    return this.currentUser.companyName
      ? this.currentUser.companyName
      : `${this.currentUser.firstName} ${this.currentUser.lastName}`;
  }

  public get isAuthenticated(): boolean {
    return !!JwtUtils.accessToken;
  }

  constructor(
    private httpClient: HttpClient,
    private msalService: MsalService,
    private broadcastService: BroadcastService,
    private userService: UserService,
    private router: Router,
    private toastrService: ToastrService,
    private commonService: CommonService,
    private environmentSettingsService: EnvironmentSettingsService,
    private route: Router,
    private signalRService: SignalRService
  ) {
    this.syncMultiTabLogins();
  }

  private syncMultiTabLogins() {
    window.addEventListener('storage', async (event) => {
      if (event.key === LocalStorageKeys.ACCESS_TOKEN) {
        if (!localStorage.getItem(LocalStorageKeys.ACCESS_TOKEN)) {
          ConsoleLogger.logInfo('Logging out due to access token being removed...');
          this.logout();
        } else if (!event.oldValue && event.newValue) {
          this.commonService.showLoader(true);
          ConsoleLogger.logInfo('Log in detected... navigating to landing page...');
          await this.loadUserInfo(JwtUtils.getUserName());
          this.navigateToLandingPage();
          this.commonService.showLoader(true);
        }
      }
    });
  }

  public get msalUser() {
    return this.msalService.getUser();
  }

  public get msalUserId() {
    return this.msalUser.displayableId;
  }
  public get msalUserName() {
    return this.msalUser.name;
  }

  public initialize() {
    const currentUserRecord: UserAccount = JSON.parse(localStorage.getItem(LocalStorageKeys.USER_INFO));
    this.setCurrentUser(currentUserRecord);

    this.broadcastService.subscribe("msal:loginFailure", (x) => {
      ConsoleLogger.logInfo('MSAL - login failure -> ', x);
      this.logout();
    });

    this.broadcastService.subscribe("msal:loginSuccess", async (x) => {
      ConsoleLogger.logInfo('MSAL - login success -> ', x);
      this.setIsInternalUserLogin(true);
    });

    this.broadcastService.subscribe("msal:acquireTokenSuccess", (x) => {
      ConsoleLogger.logInfo('MSAL - acquire token success -> ', x);
    });

    this.broadcastService.subscribe("msal:acquireTokenFailure", (x) => {
      ConsoleLogger.logInfo('MSAL - acquire token failure -> ', x);
    });

    this.broadcastService.subscribe("msal:ssoSuccess", (x) => {
      ConsoleLogger.logInfo('MSAL - SSO success -> ', x);
    });

    this.broadcastService.subscribe("msal:ssoFailure", (x) => {
      ConsoleLogger.logInfo('MSAL - SSO failure -> ', x);
    });
  }

  private setCurrentUser(userAccount: UserAccount) {
    localStorage.setItem(LocalStorageKeys.USER_INFO, JSON.stringify(userAccount));
    this.currentUserSubject.next(userAccount);
  }



  public async loginWithUserNamePassword(userName: string, userPassword: string, mfaCode: string) {
    const url = `${environment.dotNetApiUrl}/${Area.auth}/login`;
    const body = {userName, userPassword, mfaCode };

    const response = await this.httpClient.post<AuthResponse>(url, body).toPromise();
    this.setIsInternalUserLogin(false);

    if (response.responseCode === AuthResponseCode.AUTH_SUCCESS) {
      try {
        this.setAuthTokens(response.accessToken, response.refreshToken);
        await this.environmentSettingsService.initialize();

        this.signalRService.initialize();

        await this.loadUserInfo(JwtUtils.getUserName());
      } catch (err) {
        console.error(err);
        throw new Error("Invalid login. \nPlease check your login credentials or contact Aladdin for further assistance.");
      }
    }

    return response;
  }

  public async loginWithMsalAccessToken() {
    try {
      await this.msalService.loginPopup();

      const accessToken = await this.msalService.acquireTokenSilent(["User.Read", "profile"]);
      const model: EntraIdLogin = { msiToken: accessToken };

      const url = `${environment.dotNetApiUrl}/${Area.auth}/login/internal`;;
      const response = await this.httpClient.post<AuthResponse>(url, model).toPromise();

      this.setAuthTokens(response.accessToken, response.refreshToken);

      await this.loadUserInfo(JwtUtils.getUserName());
      await this.environmentSettingsService.initialize();

      this.signalRService.initialize();

      this.navigateToLandingPage();
    } catch (err) {
      console.error(err);
      throw Error("Invalid login. \nPlease check your login credentials or contact Aladdin for further assistance.");
    }
  }

  setIsInternalUserLogin(val: boolean) {
    localStorage.setItem(LocalStorageKeys.IS_EMPLOYEE, val.toString());
  }

  public loadUserInfo(email: string): Promise<string> {
    return new Promise(async (resolve, reject) => {
      try {
        if (this.currentUser) {
          resolve("Already loaded");
        } else {
          const user: UserAccount = await this.userService.getByEmail(email).toPromise();
          if (user) {
            this.setCurrentUser(user);
            resolve("Success");
          } else {
            reject("Account not found.  Please contact IT");
          }
        }
      } catch (err) {
        reject(err.error);
      }
    })
  }

  public canAccessFleetManager() {
    return JwtUtils.hasRoleByName(Roles.Developer)
      || JwtUtils.hasRoleByName(Roles.TsAdmin)
      || JwtUtils.hasRoleByName(Roles.TsUser);
  }
  public canAccessPortal() {
    return JwtUtils.hasRoleByName(Roles.Developer) ||
           JwtUtils.hasRoleByName(Roles.PortalAdmin) ||
           JwtUtils.hasRoleByName(Roles.PortalUser);
  }
  public canAccessCarrier() {
    return JwtUtils.hasRoleByName(Roles.Developer) ||
           JwtUtils.hasRoleByName(Roles.CarrierUser) ||
           JwtUtils.hasRoleByName(Roles.PortalAdmin);
  }
  public canAccessDeveloper() {
    return JwtUtils.hasRoleByName(Roles.Developer);
  }

  clearAuthSession() {
    localStorage.removeItem(LocalStorageKeys.USER_INFO);
    localStorage.removeItem(LocalStorageKeys.ACCESS_TOKEN);
    localStorage.removeItem(LocalStorageKeys.REFRESH_TOKEN);
  }


  clearMsal() {
    const keys = Object.keys(localStorage);
    const msalKeys = keys.filter(x => {
      return x.toLowerCase().indexOf('msal') > -1 || x.toLowerCase().indexOf('https://login.microsoftonline.com') > -1;
    });
    msalKeys.forEach(x => localStorage.removeItem(x));
  }

  public navigateToLandingPage(): void {
    if (this.canAccessFleetManager()) {
      this.router.navigateByUrl(Applications.fleetManager.landingPageUrl, { replaceUrl: true });
    } else if (this.canAccessPortal()) {
      this.router.navigateByUrl(Applications.portal.landingPageUrl, { replaceUrl: true });
    } else if (this.canAccessCarrier()) {
      this.router.navigateByUrl(Applications.carrier.landingPageUrl, { replaceUrl: true });
    } else {
      this.toastrService.error('Not authorized to access this application.  Please contact Aladdin IT', 'Unauthorized');
      this.logout();
    }
  }

  private async revokeAccessToken() {
    try {
      const url = `${environment.dotNetApiUrl}/${Area.auth}/revoke`;
      await this.httpClient.post<AuthResponse>(url, null).toPromise();
    } catch (err) {
      console.error('No session to revoke.');
    }
  }

  // Refresh Session
  private accessTokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(this.getAccessToken());

  getAccessToken(): string | null {
    return localStorage.getItem(LocalStorageKeys.ACCESS_TOKEN);
  }

  setAuthTokens(accessToken: string, refreshToken: string): void {
    ConsoleLogger.logInfo('Auth Session Refresh: Setting Tokens...', accessToken, refreshToken);
    localStorage.setItem(LocalStorageKeys.ACCESS_TOKEN, accessToken);
    localStorage.setItem(LocalStorageKeys.REFRESH_TOKEN, refreshToken);
    this.accessTokenSubject.next(accessToken);
  }

  refreshToken(): Observable<any> {
    ConsoleLogger.logInfo('Auth Session Refresh: Start...');
    const url = `${environment.dotNetApiUrl}/${Area.auth}/refresh`;
    const body = {accessToken: JwtUtils.accessToken, refreshToken: JwtUtils.refreshToken};

    this.isRefreshingSubject.next(true);
    const refreshRequest = this.httpClient.post<AuthResponse>(url, body)
      .pipe(
        tap((response: AuthResponse) => {
          this.setAuthTokens(response.accessToken, response.refreshToken);
          this.isRefreshingSubject.next(false);
        })
      );

    return refreshRequest;
  }

  getNewToken(): Observable<string> {
    return this.accessTokenSubject.asObservable();
  }

  async logout() {
    this.accessTokenSubject.next(null);

    clearInterval(this.tokenRetrievalInterval);
    this.clearAuthSession();
    this.clearMsal();
    this.currentUserSubject.next(null);
    this.environmentSettingsService.reset();
    this.router.navigateByUrl('', { replaceUrl: true });
  }

  public enableQueueTest() {
    this.isRefreshingSubject.next(true);
  }

  public disableQueueTest() {
    this.isRefreshingSubject.next(false);
  }
}



// WEBPACK FOOTER //
// ./src/app/auth/auth.service.ts